import { type ChangeEvent, useMemo, useState, type ReactElement } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { HeroHeader } from "@/components/ui/hero-section-1";
import { AnimatedGroup } from "@/components/ui/animated-group";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { BarList } from "@/components/ui/charts/bar-list";
import { cn } from "@/lib/utils";
import {
  calculateCostComparison,
  clampPercentage,
  type CostCalculatorResult,
  type Plan,
  type PlatformKey,
} from "@/pages/marketing/cost-calculator/calculateCostComparison";

const sanitizeNumber = (value: string): string => {
  if (value === "") {
    return "";
  }
  const sanitized = Number.parseFloat(value);
  if (Number.isNaN(sanitized)) {
    return "";
  }
  return String(sanitized);
};

const useFormatters = () => {
  return useMemo(
    () => ({
      currency: new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      percent: new Intl.NumberFormat("en-US", {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
      number: new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
      }),
    }),
    []
  );
};

type CostCalculatorChartDatum = {
  key: PlatformKey;
  label: string;
  valueLabel: string;
  percentageLabel: string;
  percentage: number;
  tone: "emerald" | "sky" | "amber" | "slate";
  hint?: string;
};

type CalculatorUiState = CostCalculatorResult & {
  barData: CostCalculatorChartDatum[];
};

const CostCalculatorPageContent = (): ReactElement => {
  const [averageOrderValue, setAverageOrderValue] = useState("15");
  const [ordersPerMonth, setOrdersPerMonth] = useState("2000");
  const [inPersonShare, setInPersonShare] = useState("90");
  const [onlineShare, setOnlineShare] = useState("10");
  const [salesTax, setSalesTax] = useState("9.75");
  const [tipRate, setTipRate] = useState("16");
  const [plan, setPlan] = useState<Plan>("starter");
  const [marketingAutomation, setMarketingAutomation] = useState(false);
  const [marketingSms, setMarketingSms] = useState("0");
  const [squareSoftwareFee, setSquareSoftwareFee] = useState("0");
  const [toastSoftwareFee, setToastSoftwareFee] = useState("69");
  const [cloverSoftwareFee, setCloverSoftwareFee] = useState("59");
  const format = useFormatters();

  const calculations = useMemo<CalculatorUiState>(() => {
    const parsedAov = Number.parseFloat(averageOrderValue) || 0;
    const parsedOrders = Number.parseInt(ordersPerMonth, 10) || 0;
    const parsedInPerson = clampPercentage(Number.parseFloat(inPersonShare) || 0);
    const parsedSalesTax = (Number.parseFloat(salesTax) || 0) / 100;
    const parsedTipRate = (Number.parseFloat(tipRate) || 0) / 100;
    const parsedMarketingSms = Math.max(0, Number.parseInt(marketingSms, 10) || 0);
    const parsedSquareSoftwareFee = Math.max(0, Number.parseFloat(squareSoftwareFee) || 0);
    const parsedToastSoftwareFee = Math.max(0, Number.parseFloat(toastSoftwareFee) || 0);
    const parsedCloverSoftwareFee = Math.max(0, Number.parseFloat(cloverSoftwareFee) || 0);

    const baseCalculations = calculateCostComparison({
      averageOrderValue: parsedAov,
      ordersPerMonth: parsedOrders,
      inPersonSharePercent: parsedInPerson,
      salesTaxRate: parsedSalesTax,
      tipRate: parsedTipRate,
      marketingAutomation,
      marketingSmsCredits: parsedMarketingSms,
      plan,
      squareSoftwareFee: parsedSquareSoftwareFee,
      toastSoftwareFee: parsedToastSoftwareFee,
      cloverSoftwareFee: parsedCloverSoftwareFee,
    });

    const maxTotal = Math.max(
      baseCalculations.totals.braise.total,
      baseCalculations.totals.square.total,
      baseCalculations.totals.toast.total,
      baseCalculations.totals.clover.total,
      0
    );

    const barData: CostCalculatorChartDatum[] = [
      {
        key: "braise",
        label: "Braise",
        valueLabel: `${format.currency.format(baseCalculations.totals.braise.total)} • AI suite included`,
        percentageLabel:
          maxTotal === 0
            ? "0%"
            : format.percent.format(baseCalculations.totals.braise.total / maxTotal),
        percentage: maxTotal === 0 ? 0 : baseCalculations.totals.braise.total / maxTotal,
        tone: "emerald",
        hint: "Includes plan & marketing choices",
      },
      {
        key: "square",
        label: "Square",
        valueLabel: format.currency.format(baseCalculations.totals.square.total),
        percentageLabel:
          maxTotal === 0
            ? "0%"
            : format.percent.format(baseCalculations.totals.square.total / maxTotal),
        percentage: maxTotal === 0 ? 0 : baseCalculations.totals.square.total / maxTotal,
        tone: "sky",
      },
      {
        key: "toast",
        label: "Toast",
        valueLabel: format.currency.format(baseCalculations.totals.toast.total),
        percentageLabel:
          maxTotal === 0
            ? "0%"
            : format.percent.format(baseCalculations.totals.toast.total / maxTotal),
        percentage: maxTotal === 0 ? 0 : baseCalculations.totals.toast.total / maxTotal,
        tone: "amber",
      },
      {
        key: "clover",
        label: "Clover",
        valueLabel: format.currency.format(baseCalculations.totals.clover.total),
        percentageLabel:
          maxTotal === 0
            ? "0%"
            : format.percent.format(baseCalculations.totals.clover.total / maxTotal),
        percentage: maxTotal === 0 ? 0 : baseCalculations.totals.clover.total / maxTotal,
        tone: "slate",
      },
    ];

    return {
      ...baseCalculations,
      barData,
    };
  }, [
    averageOrderValue,
    ordersPerMonth,
    inPersonShare,
    marketingAutomation,
    marketingSms,
    plan,
    salesTax,
    tipRate,
    squareSoftwareFee,
    toastSoftwareFee,
    cloverSoftwareFee,
    format,
  ]);

  const handleShareChange = (event: ChangeEvent<HTMLInputElement>, type: "inPerson" | "online") => {
    const nextValue = clampPercentage(Number.parseFloat(event.target.value) || 0);
    const normalized = sanitizeNumber(String(nextValue));
    if (type === "inPerson") {
      setInPersonShare(normalized);
      setOnlineShare(sanitizeNumber(String(100 - nextValue)));
    } else {
      setOnlineShare(normalized);
      setInPersonShare(sanitizeNumber(String(100 - nextValue)));
    }
  };

  const handleNumberChange = (
    setter: (value: string) => void,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setter(sanitizeNumber(event.target.value));
  };

  const orders = calculations.orders;

  return (
    <div className="min-h-screen bg-inverse text-inverse-foreground selection:bg-emerald-100 selection:text-emerald-900">
      <HeroHeader />
      <main className="relative space-y-20">
        <section className="relative isolate overflow-hidden pb-24 pt-32 md:pb-28 md:pt-36">
          <div className="absolute inset-0 -z-20 bg-radial bg-radial-[at_50%_-20%] from-emerald-500/24 via-transparent to-transparent" />
          <div className="absolute inset-0 -z-10 bg-radial bg-radial-[at_50%_100%] from-inverse-foreground/85 via-inverse-surface-strong/70 to-inverse/95" />
          <AnimatedGroup
            variants={{
              container: {
                visible: {
                  transition: { staggerChildren: 0.08, delayChildren: 0.12 },
                },
              },
              item: {
                hidden: { opacity: 0, filter: "blur(16px)", y: 22 },
                visible: {
                  opacity: 1,
                  filter: "blur(0px)",
                  y: 0,
                  transition: { type: "spring", duration: 1, bounce: 0.24 },
                },
              },
            }}
            className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-6 text-center"
          >
            <Badge className="rounded-full bg-inverse-surface/40 px-5 py-2 text-[0.65rem] uppercase tracking-[0.32em] text-inverse-muted-foreground">
              Restaurant economics
            </Badge>
            <h1 className="text-balance text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
              Restaurant Cost Calculator - Braise vs Others
            </h1>
            <p className="max-w-3xl text-lg text-inverse-muted-foreground">
              Model in-person and online processing, software fees, and marketing automation in one place.
              Tune your inputs to see where Braise keeps more margin in your pocket.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-inverse-muted-foreground/80">
              <span className="rounded-full bg-inverse-surface/40 px-4 py-2">WCAG AA friendly</span>
              <span className="rounded-full bg-inverse-surface/40 px-4 py-2">No data stored</span>
              <span className="rounded-full bg-inverse-surface/40 px-4 py-2">Live savings updates</span>
            </div>
          </AnimatedGroup>
        </section>

        <section className="relative pb-24">
          <div className="absolute inset-0 -z-10 bg-radial bg-radial-[at_50%_0%] from-inverse-highlight/18 via-transparent to-transparent" />
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 lg:flex-row">
            <Card className="w-full bg-inverse-surface/80 text-inverse-foreground ring-1 ring-inverse-border/50 backdrop-blur lg:max-w-xl">
              <CardHeader>
                <CardTitle>Inputs</CardTitle>
                <CardDescription className="text-inverse-muted-foreground/90">
                  Select realistic volumes to compare total monthly cost. In-person and online shares always sum to 100%.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <form className="space-y-6" aria-describedby="calculator-disclosure" aria-label="Restaurant cost inputs">
                  <fieldset className="grid gap-4">
                    <legend className="sr-only">Order mix</legend>
                    <div className="grid gap-2">
                      <Label htmlFor="aov">Average order value (AOV)</Label>
                      <Input
                        id="aov"
                        inputMode="decimal"
                        type="number"
                        min="0"
                        step="0.01"
                        value={averageOrderValue}
                        onChange={(event) => handleNumberChange(setAverageOrderValue, event)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="orders">Orders per month</Label>
                      <Input
                        id="orders"
                        inputMode="numeric"
                        type="number"
                        min="0"
                        step="1"
                        value={ordersPerMonth}
                        onChange={(event) => handleNumberChange(setOrdersPerMonth, event)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="in-person-share">In-person share (%)</Label>
                      <Input
                        id="in-person-share"
                        inputMode="decimal"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={inPersonShare}
                        onChange={(event) => handleShareChange(event, "inPerson")}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="online-share">Online share (%)</Label>
                      <Input
                        id="online-share"
                        inputMode="decimal"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={onlineShare}
                        onChange={(event) => handleShareChange(event, "online")}
                      />
                    </div>
                    <p className="text-sm text-inverse-muted-foreground/85" id="share-helper">
                      Shares automatically balance. Adjust either channel and the other updates to keep 100%.
                    </p>
                  </fieldset>

                  <fieldset className="grid gap-4">
                    <legend className="sr-only">Rates</legend>
                    <div className="grid gap-2">
                      <Label htmlFor="sales-tax">Sales tax (%)</Label>
                      <Input
                        id="sales-tax"
                        inputMode="decimal"
                        type="number"
                        min="0"
                        step="0.01"
                        value={salesTax}
                        onChange={(event) => handleNumberChange(setSalesTax, event)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="tip-rate">Tip rate (%)</Label>
                      <Input
                        id="tip-rate"
                        inputMode="decimal"
                        type="number"
                        min="0"
                        step="0.1"
                        value={tipRate}
                        onChange={(event) => handleNumberChange(setTipRate, event)}
                      />
                    </div>
                  </fieldset>

                  <fieldset className="grid gap-4">
                    <legend className="sr-only">Braise plan</legend>
                    <div className="grid gap-2">
                      <Label htmlFor="plan">Choose your Braise plan</Label>
                      <Select value={plan} onValueChange={(value) => setPlan(value as Plan)}>
                        <SelectTrigger id="plan">
                          <SelectValue placeholder="Select plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="starter">Starter - 0.50% of base (min $19)</SelectItem>
                          <SelectItem value="pro">Pro - $49/mo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-2 rounded-lg bg-inverse-surface/80 p-4 ring-1 ring-inverse-border/50">
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="marketing-switch" className="mb-0">
                          Marketing automation ($29/mo, 500 SMS included)
                        </Label>
                        <Switch
                          id="marketing-switch"
                          checked={marketingAutomation}
                          onCheckedChange={(checked) => setMarketingAutomation(Boolean(checked))}
                          aria-describedby={marketingAutomation ? "marketing-sms-helper" : undefined}
                        />
                      </div>
                      <p className="text-xs text-inverse-muted-foreground/85">
                        Stay in sync with automated guest journeys and SMS campaigns. Turn off when you do not need promo pushes.
                      </p>
                      {marketingAutomation ? (
                        <div className="grid gap-2">
                          <Label htmlFor="marketing-sms">Expected marketing SMS / month</Label>
                          <Input
                            id="marketing-sms"
                            inputMode="numeric"
                            type="number"
                            min="0"
                            step="1"
                            value={marketingSms}
                            onChange={(event) => handleNumberChange(setMarketingSms, event)}
                            aria-describedby="marketing-sms-helper"
                          />
                          <p className="text-xs text-inverse-muted-foreground/85" id="marketing-sms-helper">
                            First 500 SMS included. Overage billed at $0.015 each.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </fieldset>

                  <fieldset className="grid gap-4">
                    <legend className="sr-only">Competitor software</legend>
                    <div className="grid gap-2">
                      <Label htmlFor="square-fee">Square monthly software ($)</Label>
                      <Input
                        id="square-fee"
                        inputMode="decimal"
                        type="number"
                        min="0"
                        step="1"
                        value={squareSoftwareFee}
                        onChange={(event) => handleNumberChange(setSquareSoftwareFee, event)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="toast-fee">Toast monthly software ($)</Label>
                      <Input
                        id="toast-fee"
                        inputMode="decimal"
                        type="number"
                        min="0"
                        step="1"
                        value={toastSoftwareFee}
                        onChange={(event) => handleNumberChange(setToastSoftwareFee, event)}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="clover-fee">Clover monthly software ($)</Label>
                      <Input
                        id="clover-fee"
                        inputMode="decimal"
                        type="number"
                        min="0"
                        step="1"
                        value={cloverSoftwareFee}
                        onChange={(event) => handleNumberChange(setCloverSoftwareFee, event)}
                      />
                    </div>
                  </fieldset>
                </form>
              </CardContent>
            </Card>

            <div className="flex w-full flex-1 flex-col gap-6">
              <div
                className={cn(
                  "flex flex-col gap-4 rounded-3xl bg-emerald-400/10 p-6 text-left text-inverse-foreground ring-1 ring-emerald-300/40",
                  "shadow-[0_30px_120px_rgba(16,185,129,0.12)]"
                )}
                role="status"
                aria-live="polite"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-300">
                    Savings insight
                  </p>
                  <Badge className="rounded-full bg-inverse-surface/45 px-3 py-1 text-xs uppercase tracking-[0.32em] text-inverse-foreground/80">
                    Live
                  </Badge>
                </div>
                {calculations.savingsVsSquare > 0 ? (
                  <p className="text-2xl font-semibold text-inverse-foreground">
                    Capture {format.currency.format(calculations.savingsVsSquareYear)} yearly and fuel 27× AI-driven growth.
                  </p>
                ) : (
                  <p className="text-2xl font-semibold text-inverse-foreground">
                    Adjust inputs to surface annual upside and unlock 27× AI-driven growth.
                  </p>
                )}
                <p className="text-sm text-inverse-muted-foreground/90">
                  Calculations include processing, platform, and optional marketing automation for each platform.
                </p>
              </div>

              <div className="grid auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  {
                    title: "Gross monthly volume",
                    primary: format.currency.format(calculations.gmv),
                    secondary: "Includes sales tax and reflects your current inputs.",
                  },
                  {
                    title: "Monthly orders",
                    primary: format.number.format(calculations.orders),
                    secondary: `Average ticket ${format.currency.format(calculations.averageOrderValue)}.`,
                  },
                  {
                    title: "Channel mix",
                    primary: `${format.percent.format(calculations.inPersonShare)} in person · ${format.percent.format(calculations.onlineShare)} online`,
                    secondary: `${format.number.format(calculations.inPersonOrders)} venue orders / ${format.number.format(calculations.onlineOrders)} online.`,
                  },
                ].map((metric) => (
                  <Card
                    key={metric.title}
                    className="flex h-full flex-col justify-between bg-inverse-surface/80 text-inverse-foreground ring-1 ring-inverse-border/50 backdrop-blur"
                  >
                    <CardHeader className="space-y-2 pb-3">
                      <CardDescription className="text-xs font-semibold uppercase tracking-[0.28em] text-inverse-muted-foreground/80">
                        {metric.title}
                      </CardDescription>
                      <CardTitle className="text-2xl font-semibold text-inverse-foreground">{metric.primary}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1 pb-6 pt-0 text-xs text-inverse-muted-foreground/90">
                      <p>{metric.secondary}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="rounded-3xl border border-inverse-border/45 bg-gradient-to-r from-emerald-500/25 via-emerald-400/10 to-transparent p-6 shadow-[0_40px_120px_rgba(16,185,129,0.18)]">
                <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                  <div className="max-w-2xl space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.32em] text-inverse-muted-foreground">
                      AI growth engine
                    </p>
                    <p className="text-2xl font-semibold text-inverse-foreground">
                      $2,500/month of automation value is already baked into your platform fee.
                    </p>
                    <p className="text-sm text-inverse-muted-foreground/90">
                      Personalized upsells, predictive labour forecasts, churn rescues, and campaign orchestration deploy automatically-no extra contracts or hidden line items.
                    </p>
                  </div>
                  <ul className="grid gap-2 text-sm text-inverse-muted-foreground md:text-right">
                    <li>• Smart upsells recover basket value in real time</li>
                    <li>• Labour autopilot syncs staffing with projected demand</li>
                    <li>• Guest winbacks trigger the moment churn risk appears</li>
                  </ul>
                </div>
              </div>

              {calculations.planNudgeDelta >= 10 ? (
                <div className="rounded-3xl border border-border bg-card p-5 text-card-foreground shadow-md">
                  <p className="text-sm font-semibold uppercase tracking-[0.28em] text-muted-foreground">
                    Plan nudge
                  </p>
                  <p className="mt-2 text-lg font-semibold">
                    Pro saves you {format.currency.format(calculations.planNudgeDelta)} - switch plans to lock savings.
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Your Starter percentage fee exceeds $49 this month. Pro caps your platform cost with no change to processing.
                  </p>
                </div>
              ) : null}

              <div className="grid gap-5 lg:grid-cols-2">
                {Object.values(calculations.totals).map((platform) => (
                  <Card key={platform.label} className="border border-border bg-card text-card-foreground shadow-md">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center justify-between">
                        <span>{platform.label}</span>
                        <span className="text-base font-semibold text-muted-foreground">
                          {platform.label === "Braise" ? (plan === "starter" ? "Starter" : "Pro") : "Baseline"}
                        </span>
                      </CardTitle>
                      <CardDescription className="text-xs uppercase tracking-[0.28em] text-muted-foreground">
                        Total monthly cost
                      </CardDescription>
                      <p className="mt-3 text-3xl font-semibold">
                        {format.currency.format(platform.total)}
                      </p>
                    </CardHeader>
                      <CardContent className="space-y-4 text-sm text-muted-foreground">
                      <div className="flex items-center justify-between">
                        <span>Processing - in person</span>
                        <span className="font-semibold text-card-foreground">
                          {format.currency.format(platform.inPerson)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Processing - online</span>
                        <span className="font-semibold text-card-foreground">
                          {format.currency.format(platform.online)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>{platform.label === "Braise" ? "Platform & automation" : "Software fees"}</span>
                        <span className="font-semibold text-card-foreground">
                          {format.currency.format(platform.platform)}
                        </span>
                      </div>
                      <div className="h-px w-full bg-border/60" role="presentation" />
                      <div className="flex items-center justify-between text-card-foreground">
                        <span>Cost per order</span>
                        <span className="text-lg font-semibold">
                          {orders > 0 ? format.currency.format(platform.total / orders) : format.currency.format(0)}
                        </span>
                      </div>
                      {platform.label === "Braise" ? (
                        <div className="rounded-2xl border border-emerald-400/50 bg-emerald-500/15 p-5 text-xs text-card-foreground">
                          <p className="text-base font-semibold text-card-foreground">AI growth suite ($2.5K value) included</p>
                          <p className="mt-1 leading-snug">
                            Automated upsells, predictive labour, churn rescues, and campaign orchestration run on autopilot-bundled with your platform fee.
                          </p>
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-rose-400/50 bg-rose-500/10 p-5 text-xs text-card-foreground">
                          <p className="text-base font-semibold text-card-foreground">AI automation not bundled</p>
                          <p className="mt-1 leading-snug">Comparable automations require costly add-ons or third-party tools from this provider.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border border-border bg-card text-card-foreground shadow-md">
                <CardHeader className="pb-2">
                  <CardTitle className="text-inverse-foreground">Monthly totals comparison</CardTitle>
                  <CardDescription className="text-inverse-muted-foreground/85">
                    Bar length shows each platform&apos;s total relative to the highest cost.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div role="img" aria-label="Bar chart comparing total monthly costs for Braise, Square, Toast, and Clover">
                    <BarList data={calculations.barData} />
                  </div>
                  <div className="mt-4 grid gap-2 rounded-2xl border border-emerald-300/30 bg-emerald-500/10 p-5 text-xs text-inverse-muted-foreground">
                    <p className="text-sm font-semibold text-inverse-foreground">AI automation advantage</p>
                    <p>
                      Your investment unlocks a $2.5K/month AI revenue engine-upsell playbooks, labour autopilot, guest winbacks-zero add-on fees. The comparison platforms simply don&apos;t ship these capabilities.
                    </p>
                  </div>
                  {(() => {
                    const capabilityRows = [
                      "AI upsell playbooks",
                      "Predictive labour autopilot",
                      "Guest churn rescues",
                      "Campaign orchestration",
                    ];
                    return (
                      <div className="mt-5 overflow-hidden rounded-2xl border border-inverse-border/45 bg-inverse-surface/30">
                        <div className="overflow-x-auto">
                          <div className="min-w-[520px]">
                            <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-2 border-b border-inverse-border/45 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-inverse-muted-foreground/80">
                              <span>Capability</span>
                              <span className="text-right text-inverse-foreground">Braise</span>
                              <span className="text-right text-inverse-muted-foreground">Others</span>
                            </div>
                            {capabilityRows.map((label) => (
                              <div
                                key={label}
                                className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-2 px-5 py-3 text-sm text-inverse-foreground/80"
                              >
                                <span>{label}</span>
                                <span className="flex items-center justify-end gap-2 text-inverse-foreground">
                                  <CheckCircle2 className="h-4 w-4" aria-hidden />
                                  Included
                                </span>
                                <span className="flex items-center justify-end gap-2 text-inverse-muted-foreground/65">
                                  <XCircle className="h-4 w-4" aria-hidden />
                                  Not bundled
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              <div className="rounded-3xl bg-inverse-surface/80 p-6 text-sm text-inverse-muted-foreground/90 ring-1 ring-inverse-border/50" id="calculator-disclosure">
                <p className="font-semibold text-card-foreground">Assumptions & disclosure</p>
                <ul className="mt-3 list-disc space-y-2 pl-4">
                  <li>
                    Base = subtotal + tax (tips excluded). With current inputs, GMV is {format.currency.format(calculations.gmv)} and base is {format.currency.format(calculations.base)}.
                  </li>
                  <li>Processing rates follow public card-present formulas. Online processing uses 2.9% + $0.30 across all providers.</li>
                  <li>Tips never factor into Braise fees. Update shares, taxes, or software charges to match your venue.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

const CostCalculatorPage = (): ReactElement => <CostCalculatorPageContent />;

export default CostCalculatorPage;

